/**
 * Resolves the scope of an "Always Approve" decision for tooltip display.
 *
 * Mirrors the backend's `upgrade_permission_tier` mapping in
 * `modules/ai_agents/services/oversight_service.py`. Permission tiers are
 * upgraded per-capability (self-config / spawning / communication channel /
 * connected service), not per-MCP-tool, so tooltips describe the capability
 * being granted rather than the specific action name.
 *
 * NOTE on granularity: the backend currently upgrades *all* service grants of
 * a given `service_type` at the same time. Per-file granularity (e.g. upgrade
 * only the row for one Google Sheet) is a pending backend change; the tooltip
 * still names the specific resource when it is known in the payload so the
 * intent is clear in the UI.
 */
export interface AlwaysApproveScope {
  key: string;
  params?: Record<string, string>;
}

/** Human-readable labels for connected-service types. */
const SERVICE_LABELS: Record<string, string> = {
  google_sheets: 'Google Sheet',
  google_docs: 'Google Doc',
  google_drive: 'Google Drive file',
  google_calendar: 'Google Calendar',
  ms_onedrive: 'Microsoft OneDrive file',
  ms_calendar: 'Microsoft Calendar',
  ms_mail: 'Microsoft Mail',
};

function matchServiceKey(toolName: string): string | null {
  const tn = toolName.toLowerCase();
  for (const key of Object.keys(SERVICE_LABELS)) {
    if (tn.includes(key)) return key;
  }
  return null;
}

function serviceResourceLabel(serviceKey: string, resourceName?: string): string {
  const base = SERVICE_LABELS[serviceKey];
  return resourceName ? `${base} "${resourceName}"` : base;
}

export function resolveAlwaysApproveScope(
  actionType: string | undefined,
  toolName: string | undefined,
  resourceName?: string,
): AlwaysApproveScope {
  const at = (actionType ?? '').toLowerCase();
  const tn = (toolName ?? '').toLowerCase();

  // Briefing-block file writes — upgrade the specific block's access_level
  if (at === 'briefing_write' || at.startsWith('briefing_')) {
    return {
      key: 'aiChat:approvalTooltip.alwaysApproveBriefingFile',
      params: { resource: resourceName ?? 'this file' },
    };
  }

  // Self-config — matches backend's action_type prefixes
  if (at.startsWith('objectives_')) return { key: 'aiChat:approvalTooltip.alwaysApproveObjectives' };
  if (at.startsWith('schedules_')) return { key: 'aiChat:approvalTooltip.alwaysApproveSchedule' };
  if (at.startsWith('spawning_')) return { key: 'aiChat:approvalTooltip.alwaysApproveSpawning' };

  // Service grants — named connected services (Google / Microsoft)
  const serviceKey = matchServiceKey(tn);
  if (serviceKey) {
    return {
      key: 'aiChat:approvalTooltip.alwaysApproveService',
      params: { resource: serviceResourceLabel(serviceKey, resourceName) },
    };
  }

  // Communication grants
  if (at.includes('email') || tn.includes('email')) return { key: 'aiChat:approvalTooltip.alwaysApproveEmail' };
  if (at.includes('messaging') || tn.includes('send_message')) return { key: 'aiChat:approvalTooltip.alwaysApproveMessaging' };

  // Heuristic fallbacks when only tool_name is known
  if (tn.includes('schedule') || tn.includes('trigger')) return { key: 'aiChat:approvalTooltip.alwaysApproveSchedule' };
  if (tn.includes('objective')) return { key: 'aiChat:approvalTooltip.alwaysApproveObjectives' };
  if (tn.includes('spawn') || tn.includes('create_agent')) return { key: 'aiChat:approvalTooltip.alwaysApproveSpawning' };

  // Internal PanLuma resource writes (files_* / file_* actions)
  if (at.startsWith('files_') || tn.startsWith('file_') || tn.includes('file_management')) {
    return {
      key: 'aiChat:approvalTooltip.alwaysApproveService',
      params: { resource: resourceName ? `PanLuma file "${resourceName}"` : 'PanLuma files' },
    };
  }

  return { key: 'aiChat:approvalTooltip.alwaysApproveGeneric' };
}

/** Best-effort extraction of a human-readable resource name from an approval payload. */
export function extractResourceName(payload: Record<string, unknown> | null | undefined): string | undefined {
  if (!payload) return undefined;
  const keys = ['block_name', 'document_title', 'file_name', 'spreadsheet_name', 'title', 'name', 'subject'];
  for (const k of keys) {
    const v = payload[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}
